import React, { useEffect, useState, useRef, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import apiService, { SubscriptionStatus } from '../../services/api';
import { BotStatusResponse } from '../../types/bot';
import AppLayout from '../shared/AppLayout';
import { useDesktopView } from '../../hooks/useDesktopView';
import ShutdownModal from './ShutdownModal';

// Lazy load desktop component
const DesktopBotControl = lazy(() => import('./desktop/DesktopBotControl'));

// Loading fallback for desktop
const DesktopLoadingFallback: React.FC = () => (
  <div className="min-h-screen bg-black text-white flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <motion.div
        className="w-10 h-10 border-3 border-gray-600 border-t-white rounded-full"
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
      />
      <p className="text-gray-500">Loading...</p>
    </div>
  </div>
);

// Mobile Bot Control Component
const MobileBotControl: React.FC = () => {
  const [status, setStatus] = useState<BotStatusResponse | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [showShutdownModal, setShowShutdownModal] = useState(false);

  // Refs for polling intervals
  const regularPollRef = useRef<NodeJS.Timeout | null>(null);
  const fastPollRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    fetchInitialData();

    // Start regular polling every 10 seconds
    startRegularPolling();

    return () => {
      stopAllPolling();
    };
  }, []);

  const fetchInitialData = async () => {
    try {
      const [statusData, subData] = await Promise.all([
        apiService.bot.getStatus(),
        apiService.subscription.get(),
      ]);
      setStatus(statusData);
      setSubscription(subData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const startRegularPolling = () => {
    stopAllPolling();
    regularPollRef.current = setInterval(fetchStatus, 10000);
  };

  const startFastPolling = () => {
    // Stop regular polling and start fast polling (every 3 seconds)
    stopAllPolling();
    fastPollRef.current = setInterval(fetchStatus, 3000);

    // Auto-stop fast polling after 3 minutes
    setTimeout(() => {
      if (fastPollRef.current) {
        stopAllPolling();
        startRegularPolling();
      }
    }, 180000);
  };

  const stopAllPolling = () => {
    if (regularPollRef.current) {
      clearInterval(regularPollRef.current);
      regularPollRef.current = null;
    }
    if (fastPollRef.current) {
      clearInterval(fastPollRef.current);
      fastPollRef.current = null;
    }
  };

  const fetchStatus = async () => {
    try {
      const data = await apiService.bot.getStatus();
      setStatus(data);
      setLoading(false);

      // If status is stable (running or stopped), switch back to regular polling
      if (data.status !== 'starting' && data.status !== 'stopping') {
        if (fastPollRef.current) {
          stopAllPolling();
          startRegularPolling();
        }
      }

      // Clear error on successful fetch
      if (!error.includes('Failed to fetch')) {
        // Don't clear action-related errors
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch bot status');
      setLoading(false);
    }
  };

  const handleStartBot = () => {
    setShowPasswordModal(true);
    setPassword('');
    setError('');
    setSuccess('');
  };

  const confirmStartBot = async () => {
    if (!password) {
      setError('Password is required to start the bot');
      return;
    }

    setActionLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiService.bot.start(password);

      if (response.success) {
        setSuccess(response.message || 'Bot starting...');
        setShowPasswordModal(false);
        setPassword('');

        // Start fast polling to track progress
        startFastPolling();

        // Fetch immediately
        setTimeout(fetchStatus, 500);
      } else {
        setError(response.error || 'Failed to start bot');
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError('Incorrect password');
      } else {
        setError(err.response?.data?.error || err.response?.data?.message || 'Failed to start bot');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleStopBot = async () => {
    if (!window.confirm('Are you sure you want to stop the trading bot?')) {
      return;
    }

    setActionLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiService.bot.stop();

      if (response.success) {
        setSuccess(response.message || 'Bot stopping...');

        // Start fast polling to track progress
        startFastPolling();

        // Fetch immediately
        setTimeout(fetchStatus, 500);
      } else {
        setError(response.error || 'Failed to stop bot');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to stop bot');
    } finally {
      setActionLoading(false);
    }
  };

  // Determine display status
  const getStatusDisplay = () => {
    if (!status) return { text: 'Unknown', color: 'gray', pulse: false };

    switch (status.status) {
      case 'running':
        return { text: 'Running', color: 'green', pulse: true };
      case 'starting':
        return { text: 'Starting...', color: 'yellow', pulse: true };
      case 'stopping':
        return { text: 'Stopping...', color: 'yellow', pulse: true };
      case 'stopped':
        return { text: 'Stopped', color: 'gray', pulse: false };
      case 'error':
        return { text: 'Error', color: 'red', pulse: false };
      default:
        return { text: status.status || 'Unknown', color: 'gray', pulse: false };
    }
  };

  const statusDisplay = getStatusDisplay();
  const isTransitioning = status?.status === 'starting' || status?.status === 'stopping';
  const isRunning = status?.status === 'running' || status?.is_running;

  if (loading) {
    return (
      <AppLayout title="Bot Control">
        <div className="flex items-center justify-center py-20">
          <div className="text-gray-400">Loading bot status...</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Bot Control">
      {/* Status Card */}
      {status && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-900 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Current Status</h3>
            <div
              className={`flex items-center gap-2 px-3 py-1 rounded-full ${
                statusDisplay.color === 'green'
                  ? 'bg-green-500/20 text-green-400'
                  : statusDisplay.color === 'yellow'
                  ? 'bg-yellow-500/20 text-yellow-400'
                  : statusDisplay.color === 'red'
                  ? 'bg-red-500/20 text-red-400'
                  : 'bg-gray-500/20 text-gray-400'
              }`}
            >
              <div
                className={`w-2 h-2 rounded-full ${
                  statusDisplay.color === 'green'
                    ? 'bg-green-400'
                    : statusDisplay.color === 'yellow'
                    ? 'bg-yellow-400'
                    : statusDisplay.color === 'red'
                    ? 'bg-red-400'
                    : 'bg-gray-400'
                } ${statusDisplay.pulse ? 'animate-pulse' : ''}`}
              />
              <span className="font-semibold text-sm">{statusDisplay.text}</span>
            </div>
          </div>

          {status.container_id && (
            <div className="mb-3">
              <p className="text-sm text-gray-500 mb-1">Container ID</p>
              <code className="text-xs text-gray-300 bg-black/50 px-2 py-1 rounded">
                {status.container_id}
              </code>
            </div>
          )}

          {status.last_error && status.status === 'error' && (
            <div className="mb-3">
              <p className="text-sm text-gray-500 mb-1">Last Error</p>
              <p className="text-red-400 text-sm">{status.last_error}</p>
            </div>
          )}

          <div>
            <p className="text-sm text-gray-500 mb-1">Status Message</p>
            <p className="text-white">{status.message || `Bot is ${status.status}`}</p>
          </div>
        </motion.div>
      )}

      {/* Error Message */}
      {error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4"
        >
          <p className="text-red-400 text-sm">{error}</p>
        </motion.div>
      )}

      {/* Success Message */}
      {success && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-4"
        >
          <p className="text-green-400 text-sm">{success}</p>
        </motion.div>
      )}

      {/* Subscription Warning */}
      {!subscription?.is_active && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-400 font-medium">Subscription Required</p>
              <p className="text-gray-400 text-sm">An active subscription is required to start the bot.</p>
            </div>
            <Link
              to="/subscription"
              className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Subscribe
            </Link>
          </div>
        </motion.div>
      )}

      {/* Control Buttons */}
      <div className="flex gap-4 mb-6">
        {!isRunning && !isTransitioning ? (
          <motion.button
            whileTap={{ scale: 0.98 }}
            onClick={handleStartBot}
            disabled={actionLoading || isTransitioning || !subscription?.is_active}
            className="flex-1 py-4 bg-green-500 hover:bg-green-600
                     text-white font-bold rounded-xl transition-colors
                     disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {actionLoading ? 'Starting...' : 'Start Bot'}
          </motion.button>
        ) : isTransitioning ? (
          <button
            disabled
            className="flex-1 py-4 bg-yellow-500/50
                     text-white font-bold rounded-xl
                     cursor-not-allowed"
          >
            {status?.status === 'starting' ? 'Starting...' : 'Stopping...'}
          </button>
        ) : (
          <motion.button
            whileTap={{ scale: 0.98 }}
            onClick={handleStopBot}
            disabled={actionLoading || isTransitioning}
            className="flex-1 py-4 bg-red-500 hover:bg-red-600
                     text-white font-bold rounded-xl transition-colors
                     disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {actionLoading ? 'Stopping...' : 'Stop Bot'}
          </motion.button>
        )}

        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={fetchStatus}
          disabled={actionLoading}
          className="px-6 py-4 bg-gray-800 hover:bg-gray-700
                   border border-gray-700 text-white font-semibold
                   rounded-xl transition-colors disabled:opacity-50"
        >
          Refresh
        </motion.button>
      </div>

      {/* Emergency Exit Button - Only visible when running */}
      {isRunning && !isTransitioning && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <motion.button
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowShutdownModal(true)}
            disabled={actionLoading}
            className="w-full py-4 bg-red-900/50 hover:bg-red-900
                     border-2 border-red-500/50 hover:border-red-500
                     text-red-400 hover:text-red-300 font-bold rounded-xl
                     transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {actionLoading ? 'Please Wait...' : 'Emergency Exit - Close All Positions'}
          </motion.button>
          <p className="text-center text-gray-500 text-xs mt-2">
            Closes all Orca and Drift positions, withdraws funds
          </p>
        </motion.div>
      )}

      {/* Information */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-gray-900 rounded-2xl p-6"
      >
        <h4 className="text-sm font-semibold text-gray-400 mb-3">
          Important Notes
        </h4>
        <ul className="space-y-2 text-sm text-gray-500">
          <li className="flex items-start gap-2">
            <span className="text-gray-400">•</span>
            <span>
              You will need to enter your password each time you start the bot
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400">•</span>
            <span>
              The bot will automatically manage your liquidity positions on Orca
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400">•</span>
            <span>
              Monitor your positions and performance from the Vault page
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400">•</span>
            <span>
              Stopping the bot will not close your open positions
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400">•</span>
            <span>
              Bot startup may take up to 2 minutes - please be patient
            </span>
          </li>
        </ul>
      </motion.div>

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gray-900 rounded-2xl p-6 max-w-md w-full mx-4"
          >
            <h3 className="text-xl font-bold text-white mb-4">
              Enter Password to Start Bot
            </h3>
            <p className="text-gray-400 text-sm mb-4">
              Your password is required to decrypt your wallet and start the trading bot.
            </p>

            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  confirmStartBot();
                }
              }}
              placeholder="Enter your password"
              className="w-full px-4 py-3 bg-black border border-gray-700
                       text-white rounded-xl focus:outline-none focus:border-gray-500
                       transition-colors mb-4"
              autoFocus
            />

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 mb-4">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            <div className="flex gap-3">
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  setShowPasswordModal(false);
                  setPassword('');
                  setError('');
                }}
                disabled={actionLoading}
                className="flex-1 py-3 bg-gray-800 hover:bg-gray-700
                         text-gray-300 font-semibold rounded-xl transition-colors
                         disabled:opacity-50"
              >
                Cancel
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={confirmStartBot}
                disabled={actionLoading || !password}
                className="flex-1 py-3 bg-white hover:bg-gray-100
                         text-black font-bold rounded-xl transition-colors
                         disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? 'Starting...' : 'Start Bot'}
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Shutdown Modal */}
      <ShutdownModal
        isOpen={showShutdownModal}
        onClose={() => setShowShutdownModal(false)}
        onShutdownComplete={() => {
          setShowShutdownModal(false);
          setSuccess('Shutdown completed. All positions closed.');
          fetchStatus();
        }}
      />
    </AppLayout>
  );
};

// Main BotControl component with responsive switching
const BotControl: React.FC = () => {
  const { isDesktop } = useDesktopView();

  if (isDesktop) {
    return (
      <Suspense fallback={<DesktopLoadingFallback />}>
        <DesktopBotControl />
      </Suspense>
    );
  }

  return <MobileBotControl />;
};

export default BotControl;
